import { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { ConfirmDialog } from "../../components/shared/ConfirmDialog";
import { toast } from "../../components/shared/Toast";
import { useAppData } from "../../lib/AppDataContext";
import { SearchSelect } from "../../components/shared/SearchSelect";
import {
  STATUSES, STATUS_META, SOURCES, OCCUPATIONS, BUYING_PURPOSES,
  POSSESSIONS, TEMPERATURES, TEMPERATURE_META, allowsFollowUp,
  configsForLeadType,
} from "../../lib/leads";

/* Shared add/edit modal for a lead. Pass `lead={...}` to edit; pass
   null / omit to create. `onSave` receives the patched lead object;
   the parent persists it.

   Phase 2 change: assignedTo dropdown sources real employees from the
   API via activeEmployees (instead of mock seed SALES_TEAM data). Aliased
   as `allSalesPeople` so the rest of the form is unchanged. */
export function LeadForm({ lead, onSave, onClose, role = "super_admin", statusVersion }) {
  void role;
  void statusVersion;
  // Phase 2: activeEmployees = real users from /v1/users, role=employee.
  const { activeEmployees: allSalesPeople, properties, setDirtyForm } = useAppData();

  const today = new Date().toISOString().slice(0, 10);
  const init = lead || {
    status: "new", source: "Meta", sourceOther: "", mobile: "", whatsapp: "", companyName: "",
    bookingDate: today, name: "", email: "", occupation: "Salaried", occupationOther: "",
    // No default config. This used to seed ["2 BHK"], which meant every
    // manually-added lead nobody touched Config on was saved as 2 BHK —
    // and on a commercial/plot project it showed up pre-ticked and wrong.
    config: [], budget: "", budgetMin: "", budgetMax: "", buyingPurpose: "Self-use",
    reference: "", address: "", remark: "", note: "",
    possession: ["Under Construction"],
    project: "", leadType: "", temperature: "warm",
    assignedTo: [],
    followUpDate: "", followUpTime: "",
  };
  if (lead) {
    /* null -> "" so the number inputs stay controlled. Without this an
       existing lead with no budget passes value={null}, React warns, and
       the field flips between controlled and uncontrolled.

       It also matters for saving: toApiPayload treats a DEFINED budgetMin
       as an edit, so leaving these undefined on an edited lead would write
       null over a stored budget the moment someone changed the name. */
    if (init.budgetMin == null) init.budgetMin = "";
    if (init.budgetMax == null) init.budgetMax = "";
    if (init.leadType == null) init.leadType = "";
    if (typeof init.config === "string") init.config = init.config ? [init.config] : [];
    if (!Array.isArray(init.config)) init.config = [];
    if (init.occupation == null) init.occupation = "Salaried";
    if (init.buyingPurpose == null) init.buyingPurpose = "Self-use";
    if (init.note == null) init.note = "";
    if (init.sourceOther == null) init.sourceOther = "";
    if (init.occupationOther == null) init.occupationOther = "";
    if (typeof init.assignedTo === "string") init.assignedTo = init.assignedTo ? [init.assignedTo] : [];
    else if (!Array.isArray(init.assignedTo)) init.assignedTo = init.assignedTo ? [init.assignedTo] : [];
  }
  if (lead?.followUpAt && !init.followUpDate) {
    const f = lead.followUpAt;
    init.followUpDate = f.toISOString().slice(0, 10);
    init.followUpTime = String(f.getHours()).padStart(2, "0") + ":" + String(f.getMinutes()).padStart(2, "0");
  }
  if (lead && !init.bookingDate) {
    init.bookingDate = lead.createdAt?.toISOString().slice(0, 10) || today;
  }
  if (lead && init.temperature == null) init.temperature = "warm";

  const [form, setForm] = useState(init);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Clear the global dirty flag whenever this form unmounts, so a later
  // section switch never sees a stale "dirty" from a closed form.
  useEffect(() => () => setDirtyForm(false), [setDirtyForm]);
  // requestClose: confirm before discarding if the user typed anything.
  const requestClose = () => { if (dirty) { setConfirmingClose(true); } else { onClose(); } };
  const confirmDiscard = () => { setDirtyForm(false); setConfirmingClose(false); onClose(); };
  const set = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); setDirtyForm(true); };

  // Auto-fill Type from the linked property (editable afterwards).
  useEffect(() => {
    const q = (form.project || "").trim().toLowerCase();
    if (!q) return;
    const hit = (properties || []).find(
      (p) => (p.name || "").toLowerCase() === q || (p.slug || "").toLowerCase() === q
    );
    if (hit && hit.type && hit.type !== form.leadType) {
      set("leadType", hit.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project, properties]);
  const togglePossession = (p) => {
    const has = form.possession?.includes(p);
    set("possession", has ? form.possession.filter((x) => x !== p) : [...(form.possession || []), p]);
  };
  const toggleConfig = (c) => {
    const arr = Array.isArray(form.config) ? form.config : [];
    const has = arr.includes(c);
    set("config", has ? arr.filter((x) => x !== c) : [...arr, c]);
  };
  const showFollowUp = allowsFollowUp(form.status);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || form.mobile?.length !== 10) {
      toast(form.mobile?.length !== 10 ? "Mobile must be 10 digits" : "Customer name is required");
      return;
    }
    setSubmitting(true);
    let followUpAt = null;
    if (form.followUpDate && allowsFollowUp(form.status)) {
      const [hh, mm] = (form.followUpTime || "10:00").split(":").map(Number);
      const d = new Date(form.followUpDate);
      d.setHours(hh || 10, mm || 0, 0, 0);
      followUpAt = d;
    }
    const patched = {
      ...(lead || {}),
      id: lead?.id || undefined, // API generates id on create
      name: form.name.trim(),
      mobile: form.mobile,
      phone: "+91 " + form.mobile.slice(0, 5) + " " + form.mobile.slice(5),
      whatsapp: form.whatsapp || "",
      email: form.email || "",
      occupation: form.occupation || "",
      occupationOther: form.occupation === "Other" ? (form.occupationOther || "") : "",
      companyName: form.companyName || "",
      project: form.project || "",
      leadType: form.leadType || "",
      source: form.source,
      sourceOther: form.source === "Other" ? (form.sourceOther || "") : "",
      status: form.status,
      config: Array.isArray(form.config) ? form.config : (form.config ? [form.config] : []),
      budgetMin: form.budgetMin,
      budgetMax: form.budgetMax,
      buyingPurpose: form.buyingPurpose || "",
      reference: form.reference || "",
      address: form.address || "",
      remark: form.remark || "",
      note: form.note || "",
      possession: form.possession || [],
      temperature: form.temperature || "warm",
      followUpAt,
      createdAt: lead?.createdAt || new Date(form.bookingDate || today),
      assignedTo: Array.isArray(form.assignedTo)
        ? form.assignedTo
        : (form.assignedTo ? [form.assignedTo] : (lead?.assignedTo ? (Array.isArray(lead.assignedTo) ? lead.assignedTo : [lead.assignedTo]) : [])),
    };
    try {
      setDirty(false); setDirtyForm(false);
      await onSave(patched);
    } catch (err) {
      toast(err?.message || "Failed to save lead");
      setSubmitting(false);
    }
  };

  const TempPicker = () => (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">Temperature</label>
      <div className="flex gap-2">
        {TEMPERATURES.map((t) => {
          const active = form.temperature === t;
          const meta = TEMPERATURE_META[t];
          return (
            <button key={t} type="button" onClick={() => set("temperature", t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ring-inset transition-all ${active ? meta.cls + " ring-2" : "bg-white ring-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              {meta.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog open={confirmingClose} title="Discard unsaved changes?" message="You have unsaved changes in this lead. Leaving will discard them." confirmLabel="Discard & leave" onConfirm={confirmDiscard} onCancel={() => setConfirmingClose(false)} />
        <Card className="overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">{lead ? "Edit lead" : "Add lead"}</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {lead ? `Modifying ${lead.name}` : "Capture a new prospect"}
              </p>
            </div>
            <button onClick={requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>

          <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
              <SearchSelect value={form.status} onChange={(v) => set("status", v)}
                options={STATUSES.map((s) => ({ id: s, name: STATUS_META[s]?.label || s }))}
                placeholder="Select status…" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Source</label>
              <SearchSelect value={form.source} onChange={(v) => set("source", v)}
                options={SOURCES.map((s) => ({ id: s, name: s }))}
                placeholder="Select source…" />
            </div>
            {form.source === "Other" && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Specify source <span className="text-red-600">*</span></label>
                <input value={form.sourceOther || ""} onChange={(e) => set("sourceOther", e.target.value)} placeholder="e.g. Exhibition, partner referral"
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
              </div>
            )}

            {/* Project / property.

                The API does NOT accept a propertyId. leads.service.ts derives
                it via resolvePropertyId(dto.project), which does an exact
                case-insensitive match of this string against a property's
                slug OR name, and leaves the FK null on no match.

                So picking from the datalist (which stores the exact name) is
                what guarantees the link — and the link is what populates the
                Type column and the per-type config labels. Free text still
                works for projects you don't list; the lead just stays
                unlinked, exactly as before. */}
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Project / Property <span className="text-slate-400 font-normal">(pick from the list to link)</span>
              </label>
              <SearchSelect
                value={form.project || ""}
                onChange={(name) => set("project", name)}
                options={(properties || []).map((p) => ({ id: p.name, name: p.name }))}
                placeholder="Search project…"
              />
              {(() => {
                const q = (form.project || "").trim().toLowerCase();
                if (!q) {
                  return (
                    <p className="text-[10px] text-slate-400 mt-1.5">
                      Leave blank if unknown. Linking a property fills the Type column on the leads list.
                    </p>
                  );
                }
                const hit = (properties || []).find(
                  (p) => (p.name || "").toLowerCase() === q || (p.slug || "").toLowerCase() === q
                );
                const TYPE_LABEL = {
                  apartment: "Apartment", villa: "Villa", plot: "Plot",
                  duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial",
                };
                return hit ? (
                  <p className="text-[10px] text-emerald-700 mt-1.5">
                    Links to <span className="font-semibold">{hit.name}</span>
                    {hit.type ? " · " + (TYPE_LABEL[hit.type] || hit.type) : ""}
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-600 mt-1.5">
                    No matching property — the lead saves unlinked and the Type column stays empty.
                  </p>
                );
              })()}
            </div>

            {/* Property type. Auto-fills from the linked property; stays
                editable so a type can be set manually when the customer knows
                the type but not the exact property. Persisted as leadType. */}
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Type <span className="text-slate-400 font-normal">(auto-fills from the property; or set it manually)</span>
              </label>
              <SearchSelect
                value={form.leadType || ""}
                onChange={(code) => set("leadType", code)}
                options={[
                  { id: "apartment", name: "Apartment" },
                  { id: "villa", name: "Villa" },
                  { id: "plot", name: "Plot" },
                  { id: "duplex", name: "Duplex" },
                  { id: "studio", name: "Studio Apartment" },
                  { id: "commercial", name: "Commercial" },
                ]}
                placeholder="Select a type…"
              />
            </div>

            {/* Assigned-to. Real employees from /v1/users via activeEmployees. */}
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Assigned to <span className="text-slate-400 font-normal">(select one or more)</span>
              </label>
              {allSalesPeople.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
                  No active employees yet. Invite employees from Settings → Users first.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {allSalesPeople.filter((s) => s.active !== false && s.userActive !== false && s.role === "employee").map((s) => {
                    const checked = (form.assignedTo || []).includes(s.userId || s.id);  // Phase 5: prefer userId (leads.assigned_to stores user ids)
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ring-1 ring-inset cursor-pointer transition-colors ${checked ? "bg-blue-50 ring-blue-300 text-blue-900" : "ring-slate-300 hover:bg-slate-50"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const cur = form.assignedTo || [];
                            set("assignedTo", e.target.checked ? [...cur, (s.userId || s.id)] : cur.filter((x) => x !== (s.userId || s.id)));
                          }}
                          className="w-3.5 h-3.5 rounded text-blue-600"
                        />
                        <span className="text-xs font-medium">{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {(form.assignedTo || []).length === 0 && allSalesPeople.length > 0 && (
                <p className="text-[10px] text-slate-400 mt-1.5">Unassigned &middot; lead will appear in the unassigned queue.</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Customer Name <span className="text-red-600">*</span></label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Customer Mobile Number <span className="text-red-600">*</span></label>
              <div className="flex items-center gap-2 rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 focus-within:ring-blue-500">
                <span className="text-slate-500 text-sm">+91</span>
                <input type="tel" inputMode="numeric" maxLength={10} value={form.mobile}
                  onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="98XXXXXXXX" className="flex-1 outline-none text-sm tabular-nums bg-transparent" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">WhatsApp Number <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="flex items-center gap-2 rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 focus-within:ring-blue-500">
                <span className="text-slate-500 text-sm">+91</span>
                <input type="tel" inputMode="numeric" maxLength={10} value={form.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Same as mobile if blank" className="flex-1 outline-none text-sm tabular-nums bg-transparent" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Occupation</label>
              <SearchSelect value={form.occupation} onChange={(v) => set("occupation", v)}
                options={OCCUPATIONS.map((o) => ({ id: o, name: o }))}
                placeholder="Select occupation…" />
            </div>
            {form.occupation === "Other" && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Specify occupation <span className="text-red-600">*</span></label>
                <input value={form.occupationOther || ""} onChange={(e) => set("occupationOther", e.target.value)} placeholder="e.g. Doctor, freelance designer"
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Company Name <span className="text-slate-400 font-normal">(optional)</span></label>
              <input value={form.companyName} onChange={(e) => set("companyName", e.target.value)} placeholder="e.g. Infosys, TCS"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Date</label>
              <input type="date" value={form.bookingDate} onChange={(e) => set("bookingDate", e.target.value)}
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email <span className="text-slate-400 font-normal">(optional)</span></label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="customer@example.com"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>

            <div className="sm:col-span-2">
              {(() => {
                // Property type is derived from the linked property, client
                // side — the lead has no type field of its own. Blank or
                // unmatched project falls back to the union of all configs.
                const q = (form.project || "").trim().toLowerCase();
                const hit = q
                  ? (properties || []).find(
                      (p) => (p.name || "").toLowerCase() === q || (p.slug || "").toLowerCase() === q
                    )
                  : null;
                const opts = configsForLeadType(hit?.type || "");
                // Anything already stored that isn't in the active list stays
                // visible and ticked. Without this, editing an old lead would
                // render its config unticked and silently drop it on save.
                const legacy = (form.config || []).filter((c) => !opts.includes(c));
                const TYPE_LABEL = {
                  apartment: "Apartment", villa: "Villa", plot: "Plot",
                  duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial",
                };
                return (
                  <>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                      Config <span className="text-slate-400 font-normal">(select one or more)</span>
                      {hit?.type && (
                        <span className="ml-1.5 text-[10px] font-normal text-emerald-700">
                          &middot; {TYPE_LABEL[hit.type] || hit.type} options
                        </span>
                      )}
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                      {opts.map((c) => {
                        const checked = (form.config || []).includes(c);
                        return (
                          <label key={c} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ring-1 ring-inset cursor-pointer transition-colors ${checked ? "bg-blue-50 ring-blue-300 text-blue-900" : "ring-slate-300 hover:bg-slate-50"}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleConfig(c)} className="rounded text-blue-600" />
                            <span className="text-xs font-medium">{c}</span>
                          </label>
                        );
                      })}
                      {legacy.map((c) => (
                        <label key={c} title="Saved earlier — not in this property type's list. Untick to remove."
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ring-1 ring-inset cursor-pointer transition-colors bg-amber-50 ring-amber-300 text-amber-900">
                          <input type="checkbox" checked onChange={() => toggleConfig(c)} className="rounded text-amber-600" />
                          <span className="text-xs font-medium">{c}</span>
                        </label>
                      ))}
                    </div>
                    {legacy.length > 0 && (
                      <p className="text-[10px] text-amber-600 mt-1.5">
                        Amber entries were saved earlier and aren't in this type's list. They stay unless you untick them.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Two numbers in RUPEES, not one typed string. The display
                string is generated from them on save (leadsApi), matching
                Property.priceMin/priceMax so a budget can be compared
                against listings. A typed "₹1.5 Cr" cannot be compared, and
                free-text money fields have drifted from reality twice here.

                Existing leads keep their old text until re-entered -- shown
                beneath so nothing looks lost. */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Budget (₹)</label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" value={form.budgetMin} onChange={(e) => set("budgetMin", e.target.value)} placeholder="From"
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 tabular-nums focus:outline-none focus:ring-blue-500" />
                <span className="text-slate-400 text-sm">–</span>
                <input type="number" min="0" value={form.budgetMax} onChange={(e) => set("budgetMax", e.target.value)} placeholder="To"
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 tabular-nums focus:outline-none focus:ring-blue-500" />
              </div>
              {!form.budgetMin && !form.budgetMax && form.budget && (
                <p className="text-[11px] text-slate-500 mt-1">Currently: {form.budget} — enter amounts to update</p>
              )}
              <p className="text-[11px] text-slate-400 mt-1">Full rupees. 50 lakh is 5000000.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Buying Purpose</label>
              <SearchSelect value={form.buyingPurpose} onChange={(v) => set("buyingPurpose", v)}
                options={BUYING_PURPOSES.map((p) => ({ id: p, name: p }))}
                placeholder="Select buying purpose…" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Reference <span className="text-slate-400 font-normal">(optional)</span></label>
              <input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Referred by"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Address <span className="text-slate-400 font-normal">(optional)</span></label>
              <input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Locality, city"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Remark <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea value={form.remark} onChange={(e) => set("remark", e.target.value)} rows={2} placeholder="Short remark"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500 resize-none" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Note <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} placeholder="Longer notes, conversation history, special requirements"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500 resize-none" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Possession</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {POSSESSIONS.map((p) => {
                  const checked = form.possession?.includes(p);
                  return (
                    <label key={p} className={`flex items-center gap-2 px-3 py-2 rounded-lg ring-1 ring-inset cursor-pointer transition-colors ${checked ? "bg-blue-50 ring-blue-300 text-blue-900" : "ring-slate-300 hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={!!checked} onChange={() => togglePossession(p)} className="rounded text-blue-600" />
                      <span className="text-xs font-medium">{p}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="sm:col-span-2 pt-3 mt-1 border-t border-slate-200">
              <TempPicker />
            </div>

            {showFollowUp ? (
              <div className="sm:col-span-2 pt-3 mt-1 border-t border-slate-200">
                <label className="text-xs font-medium text-slate-700 mb-2 block">Schedule a follow-up <span className="text-slate-400 font-normal">(optional)</span></label>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={form.followUpDate} onChange={(e) => set("followUpDate", e.target.value)}
                    className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500" />
                  <input type="time" value={form.followUpTime} onChange={(e) => set("followUpTime", e.target.value)}
                    disabled={!form.followUpDate}
                    className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 focus:outline-none focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400" />
                </div>
              </div>
            ) : (
              <div className="sm:col-span-2 pt-3 mt-1 border-t border-slate-200">
                <div className="text-[11px] text-slate-500 bg-slate-50 ring-1 ring-inset ring-slate-200 rounded-lg px-3 py-2">
                  No follow-up needed &mdash; this lead is <span className="font-semibold">{STATUS_META[form.status]?.label || form.status}</span>.
                </div>
              </div>
            )}

            <div className="sm:col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200 mt-1">
              <Btn variant="ghost" onClick={requestClose} disabled={submitting}>Cancel</Btn>
              <Btn type="submit" variant="primary" disabled={submitting}>
                <Check className="w-4 h-4" />
                {submitting ? "Saving…" : (lead ? "Save changes" : "Add lead")}
              </Btn>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
